import type { Team } from '@compass/client/types/team';
import React, { useCallback, useMemo, useState } from 'react';

import { cn } from '@/utils';

import type { WorkforceColumn } from '../data/columns';
import { WORKFORCE_COLUMNS } from '../data/columns';
import { groupAgentsByTeam, splitCasesByTeamGroup } from '../data/teamGroups';
import type { Agent, CaseItem } from '../data/types';
import { computeGridLayout, useGridLayout } from '../hooks/useGridLayout';
import { AgentRowHeader } from './AgentRowHeader';
import { GridCell } from './GridCell';
import { TeamSeparatorRow } from './TeamSeparatorRow';

interface WorkforceGridProps {
  agents: Agent[];
  /** Real-user representation of the logged-in user. The grid doesn't render
   *  this row, but the reassign dropdowns include it so the manager can
   *  (re)assign to themselves. */
  currentUserAgent: Agent | null;
  cases: CaseItem[];
  /** The cases agent row counts come from: program-filtered but not
   *  risk-filtered, the same list `useTeamAgents` counts. */
  countedCases: CaseItem[];
  /** The selected teams, in switcher order. Agent rows are grouped under one
   *  collapsible separator per team. */
  teams: Team[];
  teamNameById: Map<string, string>;
  onReassign: (caseId: string, newAgentId: string) => Promise<void>;
  onBulkReassign: (cases: CaseItem[], newAgentId: string) => Promise<{ failed: number }>;
  bulkInFlight: boolean;
  isReassigning: boolean;
  /** Rendered in the scroll area when there are no cases to display. */
  emptyState?: React.ReactNode;
}

function columnCountTone(column: WorkforceColumn, count: number): string {
  if (count === 0) return 'text-muted-foreground';
  // Catch-all bucket — kept quiet so it doesn't compete with actionable columns.
  if (column.id === 'other') return 'text-muted-foreground';
  if (column.id === 'missing_data') return 'text-critical-foreground';
  if (column.id === 'human_call_needed') return 'text-attention-foreground';
  return 'text-foreground';
}

function countCases(cases: CaseItem[]): Pick<Agent, 'openCount' | 'breachedCount' | 'atRiskCount'> {
  const counts = { openCount: 0, breachedCount: 0, atRiskCount: 0 };
  for (const c of cases) {
    counts.openCount += 1;
    if (c.riskLevel === 'breached') counts.breachedCount += 1;
    else if (c.riskLevel === 'warning') counts.atRiskCount += 1;
  }
  return counts;
}

export function WorkforceGrid({
  agents,
  currentUserAgent,
  cases,
  countedCases,
  teams,
  teamNameById,
  onReassign,
  onBulkReassign,
  bulkInFlight,
  isReassigning,
  emptyState,
}: WorkforceGridProps) {
  const { grouped, totals } = useGridLayout(cases);
  const { pinned, groups } = useMemo(() => groupAgentsByTeam(agents, teams), [agents, teams]);
  // An agent on several teams gets one row per team, each holding only that
  // team's cases, so its counts are recomputed per row from that team's share
  // of `countedCases`.
  const { groupedByCopy, countedByCopy, multiTeamAgentIds } = useMemo(() => {
    const groupedByCopy = new Map<string, Map<string, CaseItem[]>>();
    for (const [key, copyCases] of splitCasesByTeamGroup(cases, groups)) {
      groupedByCopy.set(key, computeGridLayout(copyCases).grouped);
    }
    const countedByCopy = splitCasesByTeamGroup(countedCases, groups);
    const seen = new Set<string>();
    const multiTeamAgentIds = new Set<string>();
    for (const agent of groups.flatMap(g => g.agents)) {
      if (seen.has(agent.id)) multiTeamAgentIds.add(agent.id);
      seen.add(agent.id);
    }
    return { groupedByCopy, countedByCopy, multiTeamAgentIds };
  }, [cases, countedCases, groups]);
  // Collapsing only hides a team's rows; the column totals above come from
  // `cases`, so they never change with it.
  const [collapsedTeamIds, setCollapsedTeamIds] = useState<ReadonlySet<string>>(() => new Set());
  const toggleTeam = useCallback((teamId: string) => {
    setCollapsedTeamIds(prev => {
      const next = new Set(prev);
      if (next.has(teamId)) next.delete(teamId);
      else next.add(teamId);
      return next;
    });
  }, []);

  if (cases.length === 0 && emptyState) {
    return <div className="h-full overflow-auto">{emptyState}</div>;
  }

  const gridStyle = {
    gridTemplateColumns: `220px repeat(${WORKFORCE_COLUMNS.length}, minmax(190px, 1fr))`,
  } as const;

  // z-indices must stay below the sidebar (z-30 in AuthSidebar).
  //   Body cells              — default (z-0)
  //   Agent column (sticky-x) — z-10
  //   Header row (sticky-y)   — z-10 (same — they never overlap each other)
  //   Corner (sticky x+y)     — z-20 (must beat both)
  const headerCellBase = 'sticky top-0 z-10 bg-muted border-b border-r border-border px-3 py-2.5';
  const stickyAgentBase = 'sticky left-0 z-10 bg-card border-r border-border';

  // `isFirstRow` drops the top border: the row sits directly under the header
  // or under a team separator, which already draws one.
  const renderAgentRow = (
    agent: Agent,
    isFirstRow: boolean,
    key: string,
    cellGrouped: Map<string, CaseItem[]>,
  ) => (
    <React.Fragment key={key}>
      <div className={cn(stickyAgentBase, !isFirstRow && 'border-t')}>
        <AgentRowHeader agent={agent} />
      </div>
      {WORKFORCE_COLUMNS.map(column => (
        <GridCell
          key={column.id}
          agent={agent}
          columnId={column.id}
          columnLabel={column.label}
          cases={cellGrouped.get(`${agent.id}::${column.id}`) ?? []}
          allAgents={agents}
          currentUserAgent={currentUserAgent}
          teamNameById={teamNameById}
          onReassign={onReassign}
          onBulkReassign={onBulkReassign}
          bulkInFlight={bulkInFlight}
          isReassigning={isReassigning}
          isFirstRow={isFirstRow}
        />
      ))}
    </React.Fragment>
  );

  return (
    <div className="h-full overflow-auto">
      <div className="grid min-w-max" style={gridStyle}>
        <div
          className={cn(
            'sticky top-0 left-0 z-20 bg-muted border-b border-r border-border px-3 py-2.5',
            'text-[10px] font-semibold uppercase tracking-wider text-muted-foreground',
          )}
        >
          Agent
        </div>
        {WORKFORCE_COLUMNS.map(column => {
          const { total, atRisk } = totals[column.id];
          return (
            <div key={column.id} className={cn(headerCellBase, 'last:border-r-0')}>
              <div className="flex items-center justify-between gap-2">
                <span className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground truncate">
                  {column.label}
                </span>
                <span className="flex items-center gap-1 shrink-0">
                  <span
                    className={cn(
                      'text-xs font-semibold tabular-nums',
                      columnCountTone(column, total),
                    )}
                  >
                    {total}
                  </span>
                  {atRisk > 0 && (
                    <span className="text-[10px] text-critical-foreground tabular-nums">
                      {atRisk}
                    </span>
                  )}
                </span>
              </div>
            </div>
          );
        })}

        {pinned.map((agent, rowIdx) => renderAgentRow(agent, rowIdx === 0, agent.id, grouped))}

        {groups.map(group => {
          const collapsed = collapsedTeamIds.has(group.teamId);
          return (
            <React.Fragment key={group.teamId}>
              <TeamSeparatorRow
                teamName={group.teamName}
                agentCount={group.agents.length}
                collapsed={collapsed}
                onToggle={() => toggleTeam(group.teamId)}
              />
              {!collapsed &&
                group.agents.map((agent, rowIdx) => {
                  const copyKey = `${group.teamId}::${agent.id}`;
                  const copyGrouped = groupedByCopy.get(copyKey) ?? new Map<string, CaseItem[]>();
                  const rowAgent = multiTeamAgentIds.has(agent.id)
                    ? { ...agent, ...countCases(countedByCopy.get(copyKey) ?? []) }
                    : agent;
                  return renderAgentRow(rowAgent, rowIdx === 0, copyKey, copyGrouped);
                })}
            </React.Fragment>
          );
        })}
      </div>
    </div>
  );
}
